import type { ComponentType, ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { SegmentedControl } from './controls/SegmentedControl'
import s from './PresentationSwitcher.module.css'

export type PresentationSource = 'default' | 'requested' | 'refused'

export interface PresentationOption<T extends string> {
  value: T
  label: string
  icon?: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>
}

export interface PresentationSwitcherProps<T extends string> {
  active: T
  available: readonly T[]
  options: readonly PresentationOption<T>[]
  onChange?: (value: T) => void
  label?: string
  source?: PresentationSource
  rung?: string
  subject?: string
  testId?: string
  /** Compact relationship headers use icons; full collection toolbars use words. */
  presentation?: 'text' | 'icon'
  /** Preserve a host's established selector hook while sharing the control. */
  optionDataAttribute?: `data-${string}`
  before?: ReactNode
}

/**
 * One capability-aware presentation selector for entity collections and
 * relationship collections.
 *
 * The caller supplies the server-resolved offer and the host renderer inventory
 * already intersected. This component never invents eligibility: an unavailable
 * presentation is absent, not disabled, and a one-item offer is a statement
 * rather than a useless radio group. Provenance is kept with the control so a
 * refused personal request cannot be drawn as if it had been honoured.
 */
export function PresentationSwitcher<T extends string>({
  active, available, options, onChange, label = 'Show as', source, rung,
  subject = 'this collection', testId = 'presentation-switcher',
  presentation = 'icon', optionDataAttribute = 'data-presentation-option', before,
}: PresentationSwitcherProps<T>) {
  const byValue = new Map(options.map((option) => [option.value, option]))
  const offered = available.flatMap((value) => {
    const option = byValue.get(value)
    return option ? [option] : []
  })
  const activeLabel = byValue.get(active)?.label ?? active
  const explanation = source === 'refused'
    ? `Your choice is not available for ${subject}; showing the governed ${activeLabel} view.`
    : source === 'default'
      ? `${activeLabel} is the default for ${subject}${rung ? ` (${rung})` : ''}.`
      : null

  if (offered.length <= 1 || !onChange) {
    return explanation ? <span className={s.srOnly} data-testid={`${testId}-source`}>{explanation}</span> : null
  }

  return (
    <span className={s.root} data-testid={testId}>
      {before}
      {presentation === 'text' && <span className={s.label}>{label}</span>}
      <SegmentedControl<T>
        value={active}
        onChange={onChange}
        size="sm"
        presentation={presentation === 'text' ? 'label' : 'icon'}
        label={`${label} ${subject}`}
        testId={`${testId}-options`}
        options={offered.map((option) => {
          const Glyph = option.icon
          return {
            value: option.value,
            label: option.label,
            icon: Glyph ? <Glyph size={14} aria-hidden /> : undefined,
            testId: `${testId}-${option.value}`,
            data: { [optionDataAttribute]: option.value },
            ...(source === 'refused' && option.value === active
              ? { mark: <AlertTriangle size={10} className={s.refused} aria-hidden /> }
              : {}),
          }
        })}
      />
      {explanation && <span className={s.srOnly} data-testid={`${testId}-source`}>{explanation}</span>}
    </span>
  )
}
